'use client';

import type { IconButtonProps } from '@mui/material/IconButton';

import { useState, useCallback } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Drawer from '@mui/material/Drawer';
import Tooltip from '@mui/material/Tooltip';
import MenuItem from '@mui/material/MenuItem';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import { usePathname, useRouter } from '@/routes/hooks';
import { Iconify } from '@/components/iconify';
import { Scrollbar } from '@/components/scrollbar';
import { varAlpha } from '@/theme/styles';
import { Label } from '@/components/label';
import { SignOutButton } from '@/layouts/components/sign-out-button';
import { AnimateAvatar } from '@/components/animate/animate-avatar';
import { AccountButton } from '@/layouts/components/account-button';
import { headers } from 'next/headers';
import { sourceImage } from '@/lib/server';
import { PauseSessionButton } from './pause-session-button';

// import { paths } from 'src/routes/paths';
// import { useRouter, usePathname } from 'src/routes/hooks';
//
// import { _mock } from 'src/_mock';
// import { varAlpha } from 'src/theme/styles';
//
// import { Label } from 'src/components/label';
// import { Iconify } from 'src/components/iconify';
// import { Scrollbar } from 'src/components/scrollbar';
// import { AnimateAvatar } from 'src/components/animate';
//
// import { useMockedUser } from 'src/auth/hooks';
//
// import { UpgradeBlock } from './nav-upgrade';
// import { AccountButton } from './account-button';
// import { SignOutButton } from './sign-out-button';

// ----------------------------------------------------------------------

export type AccountDrawerProps = IconButtonProps & {
  session?: any;
  data?: {
    label: string;
    href: string;
    icon?: React.ReactNode;
    info?: React.ReactNode;
  }[];
};


export function AccountDrawer({ session, data = [], sx, ...other }: AccountDrawerProps) {
  const theme = useTheme();

  const router = useRouter();

  const pathname = usePathname();
  const user = {
    displayName: session?.name || 'John Doe',
    email: session?.email || '',
    photoURL: session?.profile?.photo?.fileName || `https://i.pravatar.cc/150?u=${session?.email || 'john.doe'}`,
    role: session?.role?.name || 'User',
    clientType: session?.role?.clientTier1?.clientType?.name || '',
  }
  const _mock = {
    fullName: (index: number) => `User ${index}`,
    image: {
      avatar: (index: number) => `https://i.pravatar.cc/150?u=user${index}`,
    },
  };

  const paths = {
    dashboard: {
      root: '/dashboard',
    },
  };

  const [open, setOpen] = useState(false);

  const handleOpenDrawer = useCallback(() => {
    setOpen(true);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setOpen(false);
  }, []);

  const handleClickItem = useCallback(
    (path: string) => {
      handleCloseDrawer();
      router.push(path);
    },
    [handleCloseDrawer, router]
  );

  const renderAvatar = (
    <AnimateAvatar
      width={96}
      slotProps={{
        avatar: { src: user?.photoURL ? sourceImage(user.photoURL) : user?.photoURL,
          alt: user?.displayName },
        overlay: {
          border: 2,
          spacing: 3,
          color: `linear-gradient(135deg, ${varAlpha(theme.vars.palette.primary.mainChannel, 0)} 25%, ${theme.vars.palette.primary.main} 100%)`,
        },
      }}
    >
      {user?.displayName?.charAt(0).toUpperCase()}
    </AnimateAvatar>
  );

  return (
    <>
      <AccountButton
        onClick={handleOpenDrawer}
        photoURL={user?.photoURL}
        displayName={user?.displayName}
        sx={sx}
        {...other}
      />

      <Drawer
        open={open}
        onClose={handleCloseDrawer}
        anchor="right"
        slotProps={{ backdrop: { invisible: true } }}
        PaperProps={{ sx: { width: 320 } }}
      >
        <IconButton
          onClick={handleCloseDrawer}
          sx={{ top: 12, left: 12, zIndex: 9, position: 'absolute' }}
        >
          <Iconify icon="mingcute:close-line" />
        </IconButton>

        <Scrollbar>
          <Stack alignItems="center" sx={{ pt: 8 }}>
            {renderAvatar}
          <Typography variant="subtitle1" noWrap sx={{ mt: 2 }}>
            {user?.displayName}
          </Typography>

          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }} noWrap>
            {user?.role}
          </Typography>

          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }} noWrap>
            {user?.email}
          </Typography>

          {user?.clientType && (
            <Label
              variant="soft"
              color="primary"
              sx={{ m: 1 }}
            >
              {user?.clientType}
            </Label>
          )}
          </Stack>

          {/*<Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="center" sx={{ p: 3 }}>*/}
          {/*  {[...Array(3)].map((_, index) => (*/}
          {/*    <Tooltip*/}
          {/*      key={_mock.fullName(index + 1)}*/}
          {/*      title={`Switch to: ${_mock.fullName(index + 1)}`}*/}
          {/*    >*/}
          {/*      <Avatar*/}
          {/*        alt={_mock.fullName(index + 1)}*/}
          {/*        src={_mock.image.avatar(index + 1)}*/}
          {/*        onClick={() => {}}*/}
          {/*      />*/}
          {/*    </Tooltip>*/}
          {/*  ))}*/}

          {/*  <Tooltip title="Add account">*/}
          {/*    <IconButton*/}
          {/*      sx={{*/}
          {/*        bgcolor: varAlpha(theme.vars.palette.grey['500Channel'], 0.08),*/}
          {/*        border: `dashed 1px ${varAlpha(theme.vars.palette.grey['500Channel'], 0.32)}`,*/}
          {/*      }}*/}
          {/*    >*/}
          {/*      <Iconify icon="mingcute:add-line" />*/}
          {/*    </IconButton>*/}
          {/*  </Tooltip>*/}
          {/*</Stack>*/}

          <Stack
            sx={{
              py: 3,
              px: 2.5,
              borderTop: `dashed 1px ${theme.vars.palette.divider}`,
              borderBottom: `dashed 1px ${theme.vars.palette.divider}`,
            }}
          >
            {data.map((option) => {
              const rootLabel = pathname.includes('/dashboard') ? 'Home' : 'Dashboard';

              const rootHref = pathname.includes('/dashboard') ? '/' : paths.dashboard.root;

              return (
                <MenuItem
                  key={option.label}
                  onClick={() => handleClickItem(option.href)}
                  sx={{
                    py: 1,
                    color: 'text.secondary',
                    '& svg': { width: 24, height: 24 },
                    '&:hover': { color: 'text.primary' },
                  }}
                >
                  {option.icon}

                  <Box component="span" sx={{ ml: 2 }}>
                    {option.label}
                  </Box>

                  {option.info && (
                    <Label color="error" sx={{ ml: 1 }}>
                      {option.info}
                    </Label>
                  )}
                </MenuItem>
              );
            })}
          </Stack>

          {/*<Box sx={{ px: 2.5, py: 3 }}>*/}
          {/*  <UpgradeBlock />*/}
          {/*</Box>*/}
        </Scrollbar>

        <Box sx={{ p: 2.5 }}>
          <Stack spacing={1.5}>
            <PauseSessionButton onClose={handleCloseDrawer} />
            <SignOutButton onClose={handleCloseDrawer} />
          </Stack>
        </Box>
      </Drawer>
    </>
  );
}
